import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { IDataTambahInvoice } from "../../constants/types";
import axios from "../../services/axios";
import Swal from "sweetalert2";
import LoadingButton from "@mui/lab/LoadingButton";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import defaultAxios, { AxiosError } from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { useMutation, useQueryClient } from "react-query";
import CloseIcon from "@mui/icons-material/Close";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Autocomplete, Card, CardContent } from "@mui/material";
import useListPenyewa from "../../services/queries/useListPenyewa";
import useRoomCoWork from "../../services/queries/useRoomCoWork";
import useRoomCoWorkExclusive from "../../services/queries/useRoomCoWorkExclusive";
import NumberFormat from "react-number-format";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ModifierArguments } from "@popperjs/core";

interface IModalTambahInvoiceProps {
    isOpenModalTambahInvoice: boolean;
    closeModalTambahInvoice: () => void;
    dataInvoice?: IDataTambahInvoice;
}

type Body = {
    tanggalInvoice: string;
    penyewaId: number;
    ruanganId: number;
    tanggalMulai?: string | null;
    tanggalBerakhir?: string | null;
    tanggal?: string | null;
    jamMulai?: string | null;
    jamBerakhir?: string | null;
    tipeHarga: string;
    nominalHarga?: number;
    keterangan?: string;
    periodeMingguan?: number;
};

type DateFilter = [Date | null, Date | null];
type TimeFilter = [string, string];

const schema = yup.object({
    tanggalInvoice: yup
        .date()
        .typeError("Tanggal invoice wajib diisi")
        .required("Tanggal invoice wajib diisi"),
    penyewaId: yup
        .number()
        .required("Penyewa wajib dipilih")
        .min(1, "Penyewa wajib dipilih"),
    ruanganId: yup
        .number()
        .required("Ruangan wajib dipilih")
        .min(1, "Ruangan wajib dipilih"),
    tanggalMulai: yup.date().nullable(),
    tanggalBerakhir: yup.date().nullable(),
    tanggal: yup.date().nullable(),
    jamMulai: yup.string().nullable(),
    jamBerakhir: yup.string().nullable(),
    tipeHarga: yup.string().required("Tipe harga wajib dipilih"),
    nominal: yup.string().when("tipeHarga", {
        is: "Custom",
        then: yup.string().required("Nominal wajib diisi"),
        otherwise: yup.string(),
    }),
    keterangan: yup.string().nullable(),
});

const CustomInput = React.forwardRef(
    ({ value, onClick, error, disabled, placeholder }: any, ref: any) => (
        <Stack
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            direction="row"
            width="100%"
            bgcolor={disabled ? "#f5f5f5" : "#FFFF"}
            height="60px"
            sx={{
                border: error ? "1px solid red" : "1px solid #cbcbcb",
                borderRadius: "4px",
                padding: 2,
                cursor: disabled ? "default" : "pointer",
            }}
            onClick={disabled ? undefined : onClick}
            ref={ref}
        >
            <Typography>{value || placeholder}</Typography>
            {!disabled && <ArrowDropDownIcon sx={{ marginRight: -1 }} />}
        </Stack>
    ),
);

CustomInput.displayName = "CustomInput";

const ModalTambahInvoice = ({
    isOpenModalTambahInvoice,
    closeModalTambahInvoice,
    dataInvoice,
}: IModalTambahInvoiceProps) => {
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isTabletScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

    const moment = require("moment");
    let now = moment().format("YYYY-MM-DD");

    const isReadOnly = dataInvoice?.isReadOnly || false;
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const initialValues: IDataTambahInvoice = React.useMemo(
        () => ({
            id: 0,
            tanggalInvoice: new Date(),
            penyewaId: 0,
            ruanganId: 0,
            tanggalMulai: null,
            tanggalBerakhir: null,
            tanggal: null,
            jamMulai: null,
            jamBerakhir: null,
            tipeHarga: "Member",
            nominal: "",
            keterangan: "",
        }),
        [],
    );

    const {
        handleSubmit,
        control,
        formState: { errors },
        setError,
        setValue,
        clearErrors,
        reset,
        watch,
    } = useForm<IDataTambahInvoice>({
        resolver: yupResolver(schema),
        defaultValues: initialValues,
    });

    const [dateRange, setDateRange] = useState<DateFilter>([
        new Date(),
        new Date(new Date().setMonth(new Date().getMonth() + 1)),
    ]);
    const [startDate, endDate] = dateRange;

    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    const [timeRange, setTimeRange] = useState<TimeFilter>(["13:00", "15:00"]);

    const [selectedKategoriRuangan, setSelectedKategoriRuangan] = useState<
        string | null
    >(null);
    const [searchRuangan, setSearchRuangan] = useState<string>("");
    const [kapasitasRuangan, setKapasitasRuangan] = useState<string>("");

    const [detailHarga, setDetailHarga] = useState<{
        hargaPerUnit: number;
        jumlahUnit: number;
        totalHarga: number;
        satuan: string;
        keterangan: string;
    } | null>(null);

    const [selectedWeekStart, setSelectedWeekStart] = useState<Date | null>(
        null,
    );
    const [selectedWeekEnd, setSelectedWeekEnd] = useState<Date | null>(null);
    const [numberOfWeeksRental, setNumberOfWeeksRental] = useState<number>(1);

    const initialParamsGetListRoom = {
        size: 100,
        page: 1,
        search: "",
        status: "Semua",
    };

    const { data: listRoom, refetch: refetchListRoom } = useRoomCoWork(
        initialParamsGetListRoom,
    );

    const { data: listRoomExclusive, refetch: refetchListRoomExclusive } =
        useRoomCoWorkExclusive(initialParamsGetListRoom);

    const initialParamsGetListPenyewa = {
        size: 100,
        page: 1,
        search: "",
        status: "semua",
        tanggalAwal: now,
        tanggalAkhir: now,
    };

    const { data: listPenyewa } = useListPenyewa(initialParamsGetListPenyewa);

    const calculateMonthsDifference = (
        startDate: Date,
        endDate: Date,
    ): number => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        const effectiveEnd = new Date(end);
        effectiveEnd.setDate(effectiveEnd.getDate() + 1);
        effectiveEnd.setHours(0, 0, 0, 0);

        let years = effectiveEnd.getFullYear() - start.getFullYear();
        let months = effectiveEnd.getMonth() - start.getMonth();
        let totalMonths = years * 12 + months;

        if (effectiveEnd.getDate() < start.getDate()) {
            const lastDayOfEffectiveEndMonth = new Date(
                effectiveEnd.getFullYear(),
                effectiveEnd.getMonth() + 1,
                0,
            );
            const isEndOfMonth =
                effectiveEnd.getDate() === lastDayOfEffectiveEndMonth.getDate();

            const lastDayOfStartMonth = new Date(
                start.getFullYear(),
                start.getMonth() + 1,
                0,
            );
            const isStartEndOfMonth =
                start.getDate() === lastDayOfStartMonth.getDate();

            if (!(isStartEndOfMonth && isEndOfMonth) && !isEndOfMonth) {
                totalMonths -= 1;
            }
        }

        return Math.max(1, totalMonths);
    };

    // const calculateDurationInWeeks = (start: Date, end: Date): number => {
    //     const diffTime = Math.abs(end.getTime() - start.getTime());
    //     const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    //     return Math.ceil(diffDays / 7);
    // };

    const [selectedPenyewa, setSelectedPenyewa] = useState<any | null>(null);
    const [selectedRuangan, setSelectedRuangan] = useState<any | null>(null);
    const [selectedHarga, setSelectedHarga] = useState<string | null>("Member");
    const [isCustomPrice, setIsCustomPrice] = useState<boolean>(false);
    const [jenisRuangan, setJenisRuangan] = useState<
        "Bulanan" | "Mingguan" | "Jam"
    >("Bulanan");

    console.log(jenisRuangan, "jns");

    const filteredRuangan = useMemo(() => {
        if (!selectedKategoriRuangan) return [];

        const allRooms = [
            ...(listRoom?.pages
                .flatMap((page) => page.results.content)
                .filter((room) => room.isAktif === 1)
                .map((room) => ({
                    ...room,
                    isKhusus: false,
                    jenisSewa:
                        room.jenisSewa === "bulanan"
                            ? "Bulanan"
                            : room.jenisSewa === "jam"
                              ? "Jam"
                              : room.jenisSewa === "mingguan"
                                ? "Mingguan"
                                : "Bulanan",
                    kategori: "Ruangan Biasa",
                })) ?? []),
            ...(listRoomExclusive?.pages
                .flatMap((page) => page.results.content)
                .filter((room) => room.isAktif === 1)
                .map((room) => ({
                    ...room,
                    isKhusus: true,
                    jenisSewa:
                        room.jenisSewa === "bulanan"
                            ? "Bulanan"
                            : room.jenisSewa === "jam"
                              ? "Jam"
                              : room.jenisSewa === "mingguan"
                                ? "Mingguan"
                                : "Bulanan",
                    kategori: "Ruangan Khusus",
                })) ?? []),
        ];

        let filtered = allRooms;
        if (selectedKategoriRuangan === "Ruangan Biasa") {
            filtered = allRooms.filter((room) => !room.isKhusus);
        } else if (selectedKategoriRuangan === "Ruangan Khusus") {
            filtered = allRooms.filter((room) => room.isKhusus);
        }

        if (searchRuangan) {
            filtered = filtered.filter((room) =>
                room.nama.toLowerCase().includes(searchRuangan.toLowerCase()),
            );
        }

        return filtered;
    }, [selectedKategoriRuangan, searchRuangan, listRoom, listRoomExclusive]);

    const watchNominal = watch("nominal");

    const calculateDetailHarga = useMemo(() => {
        if (!selectedRuangan || !selectedHarga) return null;

        let hargaPerUnit = 0;
        let jumlahUnit = 0;
        let satuan = "";
        let keterangan = "";

        if (selectedRuangan.isKhusus) {
            if (selectedHarga === "Member") {
                hargaPerUnit = selectedRuangan.hargaSewaMember || 0;
            } else if (selectedHarga === "Non Member") {
                hargaPerUnit = selectedRuangan.hargaSewaNonMember || 0;
            } else if (selectedHarga === "Custom") {
                const nominalValue =
                    watchNominal?.toString().replace(/\./g, "") || "0";
                hargaPerUnit = parseInt(nominalValue) || 0;
            }
        } else {
            if (selectedHarga === "Member") {
                hargaPerUnit = selectedRuangan.hargaSewaBulananMember || 0;
            } else if (selectedHarga === "Non Member") {
                hargaPerUnit = selectedRuangan.hargaSewaBulananNonMember || 0;
            } else if (selectedHarga === "Custom") {
                const nominalValue =
                    watchNominal?.toString().replace(/\./g, "") || "0";
                hargaPerUnit = parseInt(nominalValue) || 0;
            }
        }

        if (jenisRuangan === "Bulanan") {
            if (startDate && endDate) {
                const diffMonths = calculateMonthsDifference(
                    startDate,
                    endDate,
                );
                jumlahUnit = diffMonths;
                satuan = "Bulan";
                const startFormatted = startDate.toLocaleDateString("id-ID", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                });
                const endFormatted = endDate.toLocaleDateString("id-ID", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                });
                keterangan = `${startFormatted} - ${endFormatted} (${diffMonths} bulan)`;
            }
        } else if (jenisRuangan === "Mingguan") {
            if (selectedWeekStart && selectedWeekEnd) {
                const diffTime = Math.abs(
                    selectedWeekEnd.getTime() - selectedWeekStart.getTime(),
                );
                const diffDays =
                    Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                const diffWeeks = numberOfWeeksRental;
                jumlahUnit = diffWeeks;
                satuan = "Minggu";
                keterangan = `${diffDays} hari (${diffWeeks} minggu)`;
            }
        } else if (jenisRuangan === "Jam") {
            if (timeRange[0] && timeRange[1]) {
                const [startHour, startMinute] = timeRange[0]
                    .split(":")
                    .map(Number);
                const [endHour, endMinute] = timeRange[1]
                    .split(":")
                    .map(Number);

                const startTotalMinutes = startHour * 60 + startMinute;
                const endTotalMinutes = endHour * 60 + endMinute;

                let diffMinutes = endTotalMinutes - startTotalMinutes;
                if (diffMinutes < 0) {
                    diffMinutes += 24 * 60;
                }

                const diffHours = diffMinutes / 60;
                jumlahUnit = diffHours;
                satuan = "Jam";
                keterangan = `${timeRange[0]} - ${timeRange[1]} (${diffHours.toFixed(2)} jam)`;
            }
        }

        const totalHarga = hargaPerUnit * jumlahUnit;

        if (selectedHarga === "Custom") {
            return {
                hargaPerUnit,
                jumlahUnit,
                totalHarga,
                satuan,
                keterangan,
            };
        }

        if (hargaPerUnit > 0 && jumlahUnit > 0) {
            return {
                hargaPerUnit,
                jumlahUnit,
                totalHarga,
                satuan,
                keterangan,
            };
        }

        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedRuangan,
        selectedHarga,
        jenisRuangan,
        startDate,
        endDate,
        selectedWeekStart,
        selectedWeekEnd,
        timeRange,
        watchNominal,
    ]);

    useEffect(() => {
        if (calculateDetailHarga) {
            setDetailHarga(calculateDetailHarga);

            if (
                selectedHarga !== "Custom" &&
                calculateDetailHarga.totalHarga > 0
            ) {
                setValue(
                    "nominal",
                    calculateDetailHarga.hargaPerUnit.toString(),
                );
            }
        } else {
            setDetailHarga(null);
        }
    }, [calculateDetailHarga, selectedHarga, setValue]);

    useEffect(() => {
        if (selectedHarga === "Custom" && selectedRuangan) {
            setDetailHarga(null);

            const timer = setTimeout(() => {
                const recalculated = calculateDetailHarga;
                if (recalculated) {
                    setDetailHarga(recalculated);
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [selectedHarga, selectedRuangan, calculateDetailHarga]);

    useEffect(() => {
        if (selectedHarga === "Custom" && selectedRuangan) {
            // const nominalValue =
            //     watchNominal?.toString().replace(/\./g, "") || "0";
            const recalculated = calculateDetailHarga;
            if (recalculated) {
                setDetailHarga(recalculated);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchNominal, selectedHarga, selectedRuangan, calculateDetailHarga]);

    // Fungsi untuk menangani perubahan periode mingguan
    const handleWeeklyPeriodChange = (date: Date | null) => {
        if (!date || isReadOnly) return;

        // Set tanggal mulai ke tanggal yang dipilih
        const startDate = new Date(date);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + numberOfWeeksRental * 7 - 1);
        endDate.setHours(23, 59, 59, 999);

        setSelectedWeekStart(startDate);
        setSelectedWeekEnd(endDate);

        // Update form values
        setValue("tanggalMulai", startDate.toISOString());
        setValue("tanggalBerakhir", endDate.toISOString());
        clearErrors("tanggalMulai");
        clearErrors("tanggalBerakhir");

        console.log("📅 Period changed:", {
            start: startDate.toLocaleDateString("id-ID"),
            end: endDate.toLocaleDateString("id-ID"),
            weeks: numberOfWeeksRental,
            totalDays: numberOfWeeksRental * 7,
        });
    };

    // Fungsi untuk menangani perubahan jumlah minggu
    const handleNumberOfWeeksChange = (weeks: number) => {
        setNumberOfWeeksRental(weeks);

        if (selectedWeekStart) {
            const endDate = new Date(selectedWeekStart);
            endDate.setDate(selectedWeekStart.getDate() + weeks * 7 - 1);
            endDate.setHours(23, 59, 59, 999);

            setSelectedWeekEnd(endDate);
            setValue("tanggalBerakhir", endDate.toISOString());
            clearErrors("tanggalBerakhir");

            console.log("📆 Weeks changed:", {
                weeks,
                start: selectedWeekStart.toLocaleDateString("id-ID"),
                end: endDate.toLocaleDateString("id-ID"),
                totalDays: weeks * 7,
            });
        }
    };

    // Inisialisasi periode mingguan saat komponen mount atau reset
    useEffect(() => {
        if (
            isOpenModalTambahInvoice &&
            jenisRuangan === "Mingguan" &&
            !selectedWeekStart
        ) {
            const today = new Date();

            // Set default ke hari ini
            const startDate = new Date(today);

            const endDate = new Date(today);
            endDate.setDate(today.getDate() + numberOfWeeksRental * 7 - 1);

            setSelectedWeekStart(startDate);
            setSelectedWeekEnd(endDate);

            setValue("tanggalMulai", startDate.toISOString());
            setValue("tanggalBerakhir", endDate.toISOString());

            console.log("🚀 Modal dibuka - Default period set:", {
                start: startDate.toLocaleDateString("id-ID"),
                end: endDate.toLocaleDateString("id-ID"),
                weeks: numberOfWeeksRental,
                totalDays: numberOfWeeksRental * 7,
            });
        }
    }, [
        isOpenModalTambahInvoice,
        jenisRuangan,
        selectedWeekStart,
        setValue,
        numberOfWeeksRental,
    ]);

    useEffect(() => {
        if (selectedRuangan && selectedHarga && !isCustomPrice) {
            updateNominalHarga(selectedRuangan, selectedHarga);
            setValue("ruanganId", selectedRuangan.id);

            setKapasitasRuangan(selectedRuangan.kapasitas?.toString() || "0");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRuangan, selectedHarga, setValue, isCustomPrice]);

    useEffect(() => {
        if (selectedPenyewa) {
            setValue("penyewaId", selectedPenyewa.id);
        }
    }, [selectedPenyewa, setValue]);

    useEffect(() => {
        if (
            startDate &&
            (jenisRuangan === "Bulanan" || jenisRuangan === "Mingguan")
        ) {
            setValue("tanggalMulai", startDate.toISOString());
        }
        if (
            endDate &&
            (jenisRuangan === "Bulanan" || jenisRuangan === "Mingguan")
        ) {
            setValue("tanggalBerakhir", endDate.toISOString());
        }
    }, [startDate, endDate, jenisRuangan, setValue]);

    useEffect(() => {
        if (selectedDate && jenisRuangan === "Jam") {
            setValue("tanggal", selectedDate.toISOString());
        }
    }, [selectedDate, jenisRuangan, setValue]);

    useEffect(() => {
        if (timeRange[0] && jenisRuangan === "Jam") {
            setValue("jamMulai", timeRange[0]);
        }
        if (timeRange[1] && jenisRuangan === "Jam") {
            setValue("jamBerakhir", timeRange[1]);
        }
    }, [timeRange, jenisRuangan, setValue]);

    useEffect(() => {
        if (selectedHarga && !dataInvoice) {
            setValue("tipeHarga", selectedHarga);
            if (selectedHarga === "Custom") {
                setIsCustomPrice(true);
                setValue("nominal", "");
            } else {
                setIsCustomPrice(false);
                if (selectedRuangan) {
                    updateNominalHarga(selectedRuangan, selectedHarga);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedHarga, setValue, selectedRuangan]);

    const formatDateToYYYYMMDD = (date: Date | null): string | null => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // Handle perubahan bulanan - tetap simpan tanggal lengkap
    const handleMonthlyChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        setDateRange([start, end]);

        if (start) {
            setValue("tanggalMulai", start.toISOString());
            clearErrors("tanggalMulai");
        }

        if (end) {
            setValue("tanggalBerakhir", end.toISOString());
            clearErrors("tanggalBerakhir");
        }

        if (start && end) {
            const diffMonths = calculateMonthsDifference(start, end);
            console.log(`📅 Periode Bulanan berubah:
                Start: ${start.toLocaleDateString("id-ID")}
                End: ${end.toLocaleDateString("id-ID")}
                Effective End: ${new Date(end.getTime() + 86400000).toLocaleDateString("id-ID")}
                Total Bulan: ${diffMonths}
            `);
        }
    };

    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (values: {
            body: Body;
            isEdit: boolean;
            id?: number;
        }) => {
            const { body, isEdit, id } = values;

            const formData = new FormData();
            formData.append("tanggalInvoice", body.tanggalInvoice);
            formData.append("penyewaId", String(body.penyewaId));
            formData.append("ruanganId", String(body.ruanganId));
            formData.append(
                "tipeHarga",
                body.tipeHarga.toLowerCase() === "custom"
                    ? "custom"
                    : body.tipeHarga.toLowerCase() === "non member"
                      ? "nonMember"
                      : body.tipeHarga.toLowerCase(),
            );

            if (jenisRuangan === "Bulanan") {
                formData.append("tanggalMulai", body.tanggalMulai ?? "");
                formData.append("tanggalBerakhir", body.tanggalBerakhir ?? "");
                formData.append("jamMulai", body.jamMulai ?? "");
                formData.append("jamBerakhir", body.jamBerakhir ?? "");
            } else if (jenisRuangan === "Mingguan") {
                formData.append("tanggalMulai", body.tanggalMulai ?? "");
                formData.append(
                    "periodeMingguan",
                    String(body.periodeMingguan ?? 1),
                );
                formData.append("jamMulai", body.jamMulai ?? "");
                formData.append("jamBerakhir", body.jamBerakhir ?? "");
            } else if (jenisRuangan === "Jam") {
                formData.append("tanggal", body.tanggal ?? "");
                formData.append("jamMulai", body.jamMulai ?? "");
                formData.append("jamBerakhir", body.jamBerakhir ?? "");
            }

            if (
                body.tipeHarga.toLowerCase() === "custom" &&
                body.nominalHarga
            ) {
                formData.append("nominalHarga", String(body.nominalHarga));
            }

            if (body.keterangan) {
                formData.append("keterangan", body.keterangan);
            }

            if (isEdit && id) {
                formData.append("_method", "put");
                const res = await axios.post(
                    `/api/invoice-sewa/${id}`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    },
                );
                return res.data;
            } else {
                const res = await axios.post(`/api/invoice-sewa`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
                return res.data;
            }
        },
    });

    const onSubmit = (values: IDataTambahInvoice) => {
        setIsButtonLoading(true);

        if (!selectedPenyewa) {
            setError("penyewaId", {
                type: "manual",
                message: "Penyewa wajib dipilih",
            });
            setIsButtonLoading(false);
            return;
        }

        if (!selectedRuangan) {
            setError("ruanganId", {
                type: "manual",
                message: "Ruangan wajib dipilih",
            });
            setIsButtonLoading(false);
            return;
        }

        if (jenisRuangan === "Bulanan") {
            if (!startDate || !endDate) {
                if (!startDate)
                    setError("tanggalMulai", {
                        type: "manual",
                        message: "Tanggal mulai wajib diisi",
                    });
                if (!endDate)
                    setError("tanggalBerakhir", {
                        type: "manual",
                        message: "Tanggal berakhir wajib diisi",
                    });
                setIsButtonLoading(false);
                return;
            }

            // Validasi minimal 1 bulan
            const diffMonths = calculateMonthsDifference(startDate, endDate);
            if (diffMonths < 1) {
                Swal.fire({
                    title: "Periode Tidak Valid",
                    text: "Periode sewa minimal 1 bulan",
                    icon: "warning",
                    confirmButtonColor: "#45A779",
                });
                setIsButtonLoading(false);
                return;
            }
        } else if (jenisRuangan === "Mingguan") {
            if (!selectedWeekStart) {
                setError("tanggalMulai", {
                    type: "manual",
                    message: "Tanggal mulai wajib diisi",
                });
                setIsButtonLoading(false);
                return;
            }
            if (!numberOfWeeksRental || numberOfWeeksRental < 1) {
                setIsButtonLoading(false);
                return;
            }
        } else if (jenisRuangan === "Jam") {
            if (!selectedDate) {
                setError("tanggal", {
                    type: "manual",
                    message: "Tanggal wajib diisi",
                });
                setIsButtonLoading(false);
                return;
            }
            if (!timeRange[0] || !timeRange[1]) {
                setError("jamMulai", {
                    type: "manual",
                    message: "Jam mulai dan berakhir wajib diisi",
                });
                setIsButtonLoading(false);
                return;
            }
        }

        if (!selectedHarga) {
            setError("tipeHarga", {
                type: "manual",
                message: "Tipe harga wajib dipilih",
            });
            setIsButtonLoading(false);
            return;
        }

        if (
            selectedHarga === "Custom" &&
            (!values.nominal || values.nominal === "0")
        ) {
            setError("nominal", {
                type: "manual",
                message: "Nominal wajib diisi untuk harga custom",
            });
            setIsButtonLoading(false);
            return;
        }

        const body: Body = {
            tanggalInvoice: values.tanggalInvoice
                ? new Date(values.tanggalInvoice).toISOString().split("T")[0]
                : new Date().toISOString().split("T")[0],
            penyewaId: values.penyewaId,
            ruanganId: values.ruanganId,
            tipeHarga:
                values.tipeHarga === "non member"
                    ? "nonMember"
                    : values.tipeHarga === "Member"
                      ? "member"
                      : values.tipeHarga.toLowerCase(),
            keterangan: values.keterangan ?? "",
        };

        if (jenisRuangan === "Bulanan") {
            // Kirim tanggal lengkap ke backend (termasuk hari)
            body.tanggalMulai = formatDateToYYYYMMDD(startDate);
            body.tanggalBerakhir = formatDateToYYYYMMDD(endDate);
            body.jamMulai = timeRange[0];
            body.jamBerakhir = timeRange[1];
        } else if (jenisRuangan === "Mingguan") {
            body.tanggalMulai = formatDateToYYYYMMDD(selectedWeekStart);
            body.periodeMingguan = numberOfWeeksRental;
            body.jamMulai = timeRange[0];
            body.jamBerakhir = timeRange[1];
        } else if (jenisRuangan === "Jam") {
            body.tanggal = formatDateToYYYYMMDD(selectedDate);
            body.jamMulai = timeRange[0];
            body.jamBerakhir = timeRange[1];
        }

        if (selectedHarga === "Custom" && values.nominal) {
            const nominalValue = values.nominal.toString().replace(/\./g, "");
            body.nominalHarga = parseInt(nominalValue);
        }

        console.log("📤 Data yang dikirim:", {
            body,
            jenisRuangan,
            isMingguan: jenisRuangan === "Mingguan",
            periodeMingguan: body.periodeMingguan,
        });

        mutation.mutate(
            {
                body,
                isEdit: !!dataInvoice,
                id: dataInvoice?.id,
            },
            {
                onSuccess: () => {
                    setIsButtonLoading(false);
                    queryClient.invalidateQueries("listInvoicePenyewa");
                    Swal.fire({
                        title: dataInvoice
                            ? "Invoice berhasil diubah"
                            : "Invoice berhasil ditambahkan",
                        position: "top-end",
                        showConfirmButton: false,
                        icon: "success",
                        toast: true,
                        timer: 3000,
                        timerProgressBar: true,
                        showCloseButton: true,
                        customClass: {
                            container: "my-swal",
                        },
                    });
                    closeModalTambahInvoice();
                    reset(initialValues);
                    setSelectedHarga("Member");
                    setIsCustomPrice(false);
                    setJenisRuangan("Bulanan");
                    setSelectedKategoriRuangan(null);
                    setSearchRuangan("");
                    setKapasitasRuangan("");
                    setDetailHarga(null);
                    refetchListRoom();
                    refetchListRoomExclusive();

                    handleCloseModal();
                },
                onError: (error) => {
                    setIsButtonLoading(false);
                    if (defaultAxios.isAxiosError(error)) {
                        const serverError = error as AxiosError<any>;
                        if (serverError && serverError.response) {
                            Swal.fire({
                                title: "Terjadi Kesalahan!",
                                text: `${serverError.response.data.message}`,
                                icon: "error",
                                confirmButtonColor: "#45A779",
                                customClass: {
                                    container: "my-swal",
                                },
                            });
                        } else {
                            Swal.fire({
                                title: "Terjadi Kesalahan!",
                                text: `Telah Terjadi Kesalahan`,
                                icon: "error",
                                confirmButtonColor: "#45A779",
                                customClass: {
                                    container: "my-swal",
                                },
                            });
                        }
                    }
                },
            },
        );
    };

    const handleRuanganChange = (newValue: any) => {
        if (!isReadOnly) {
            setSelectedRuangan(newValue);
            if (newValue) {
                setValue("ruanganId", newValue.id);
                clearErrors("ruanganId");

                setKapasitasRuangan(newValue.kapasitas?.toString() || "0");

                if (newValue.isKhusus) {
                    if (newValue.jenisSewa === "Jam") {
                        setJenisRuangan("Jam");
                    } else if (newValue.jenisSewa === "Mingguan") {
                        setJenisRuangan("Mingguan");

                        const today = new Date();
                        const startDate = new Date(today);

                        const endDate = new Date(today);
                        endDate.setDate(
                            today.getDate() + numberOfWeeksRental * 7 - 1,
                        );

                        setSelectedWeekStart(startDate);
                        setSelectedWeekEnd(endDate);

                        setValue("tanggalMulai", startDate.toISOString());
                        setValue("tanggalBerakhir", endDate.toISOString());

                        console.log(
                            "🏢 Ruangan Mingguan dipilih - Default period:",
                            {
                                start: startDate.toLocaleDateString("id-ID"),
                                end: endDate.toLocaleDateString("id-ID"),
                                weeks: numberOfWeeksRental,
                                totalDays: numberOfWeeksRental * 7,
                            },
                        );
                    } else {
                        setJenisRuangan("Bulanan");
                        // Set default untuk bulanan ke bulan ini
                        const today = new Date();
                        const startOfMonth = new Date(
                            today.getFullYear(),
                            today.getMonth(),
                            1,
                        );
                        const endOfMonth = new Date(
                            today.getFullYear(),
                            today.getMonth() + 1,
                            0,
                        );
                        setDateRange([startOfMonth, endOfMonth]);
                        setValue("tanggalMulai", startOfMonth.toISOString());
                        setValue("tanggalBerakhir", endOfMonth.toISOString());
                    }
                } else {
                    if (newValue.jenisSewa === "Jam") {
                        setJenisRuangan("Jam");
                    } else if (newValue.jenisSewa === "Mingguan") {
                        setJenisRuangan("Mingguan");

                        const today = new Date();
                        const startDate = new Date(today);

                        const endDate = new Date(today);
                        endDate.setDate(
                            today.getDate() + numberOfWeeksRental * 7 - 1,
                        );

                        setSelectedWeekStart(startDate);
                        setSelectedWeekEnd(endDate);

                        setValue("tanggalMulai", startDate.toISOString());
                        setValue("tanggalBerakhir", endDate.toISOString());

                        console.log(
                            "🏢 Ruangan Mingguan dipilih - Default period:",
                            {
                                start: startDate.toLocaleDateString("id-ID"),
                                end: endDate.toLocaleDateString("id-ID"),
                                weeks: numberOfWeeksRental,
                                totalDays: numberOfWeeksRental * 7,
                            },
                        );
                    } else {
                        setJenisRuangan("Bulanan");
                        // Set default untuk bulanan ke bulan ini
                        const today = new Date();
                        const startOfMonth = new Date(
                            today.getFullYear(),
                            today.getMonth(),
                            1,
                        );
                        const endOfMonth = new Date(
                            today.getFullYear(),
                            today.getMonth() + 1,
                            0,
                        );
                        setDateRange([startOfMonth, endOfMonth]);
                        setValue("tanggalMulai", startOfMonth.toISOString());
                        setValue("tanggalBerakhir", endOfMonth.toISOString());
                    }
                }

                if (!isCustomPrice && selectedHarga) {
                    updateNominalHarga(newValue, selectedHarga);
                }
            } else {
                setValue("ruanganId", 0);
                setKapasitasRuangan("");
                setJenisRuangan("Bulanan");
                if (!isCustomPrice) {
                    setValue("nominal", "");
                }
            }
        }
    };

    const updateNominalHarga = (ruangan: any, tipeHarga: string) => {
        let nominal = "";

        if (ruangan.isKhusus) {
            if (tipeHarga === "Member") {
                nominal = String(ruangan.hargaSewaMember || 0);
            } else if (tipeHarga === "Non Member") {
                nominal = String(ruangan.hargaSewaNonMember || 0);
            }
        } else {
            if (tipeHarga === "Member") {
                nominal = String(ruangan.hargaSewaBulananMember || 0);
            } else if (tipeHarga === "Non Member") {
                nominal = String(ruangan.hargaSewaBulananNonMember || 0);
            }
        }

        setValue("nominal", nominal);
    };

    const timeOptions = Array.from({ length: 24 * 4 }, (_, i) => {
        const hours = Math.floor(i / 4);
        const minutes = (i % 4) * 15;
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    });

    useEffect(() => {
        if (isOpenModalTambahInvoice && dataInvoice?.jenisSewa) {
            const jenisSewaMap: {
                [key: string]: "Bulanan" | "Mingguan" | "Jam";
            } = {
                bulanan: "Bulanan",
                mingguan: "Mingguan",
                jam: "Jam",
            };
            const mappedJenisSewa =
                jenisSewaMap[dataInvoice.jenisSewa.toLowerCase()];
            if (mappedJenisSewa) {
                setJenisRuangan(mappedJenisSewa);
            }
        }
    }, [isOpenModalTambahInvoice, dataInvoice]);

    useEffect(() => {
        if (isOpenModalTambahInvoice && dataInvoice) {
            reset(initialValues);

            setValue("id", dataInvoice.id || 0);

            setValue(
                "tanggalInvoice",
                dataInvoice.tanggalInvoice
                    ? new Date(dataInvoice.tanggalInvoice)
                    : new Date(),
            );
            setValue("penyewaId", dataInvoice.penyewaId || 0);
            setValue("ruanganId", dataInvoice.ruanganId || 0);
            setValue("keterangan", dataInvoice.keterangan || "");

            let uiTipeHarga = "Member";
            if (dataInvoice.tipeHarga === "member") {
                uiTipeHarga = "Member";
            } else if (dataInvoice.tipeHarga === "nonMember") {
                uiTipeHarga = "Non Member";
            } else if (dataInvoice.tipeHarga === "custom") {
                uiTipeHarga = "Custom";
            }

            setValue("tipeHarga", uiTipeHarga);

            if (dataInvoice.penyewaId && listPenyewa?.content) {
                const penyewa = listPenyewa.content.find(
                    (p) => p.id === dataInvoice.penyewaId,
                );
                setSelectedPenyewa(penyewa || null);
            }

            if (dataInvoice.ruanganId) {
                const allRooms = [
                    ...(listRoom?.pages
                        .flatMap((page) => page.results.content)
                        .map((room) => ({
                            ...room,
                            isKhusus: false,
                            jenisSewa:
                                room.jenisSewa === "bulanan"
                                    ? "Bulanan"
                                    : room.jenisSewa === "jam"
                                      ? "Jam"
                                      : room.jenisSewa === "mingguan"
                                        ? "Mingguan"
                                        : "Bulanan",
                            kategori: "Ruangan Biasa",
                        })) || []),
                    ...(listRoomExclusive?.pages
                        .flatMap((page) => page.results.content)
                        .map((room) => ({
                            ...room,
                            isKhusus: true,
                            jenisSewa:
                                room.jenisSewa === "bulanan"
                                    ? "Bulanan"
                                    : room.jenisSewa === "jam"
                                      ? "Jam"
                                      : room.jenisSewa === "mingguan"
                                        ? "Mingguan"
                                        : "Bulanan",
                            kategori: "Ruangan Khusus",
                        })) || []),
                ];

                const ruangan = allRooms.find(
                    (r) => r.id === dataInvoice.ruanganId,
                );
                if (ruangan) {
                    setSelectedRuangan(ruangan);
                    setSelectedKategoriRuangan(ruangan.kategori);

                    setKapasitasRuangan(ruangan.kapasitas?.toString() || "0");

                    if (ruangan.isKhusus) {
                        if (ruangan.jenisSewa === "Jam") {
                            setJenisRuangan("Jam");
                        } else if (ruangan.jenisSewa === "Mingguan") {
                            setJenisRuangan("Mingguan");
                            if (
                                dataInvoice.tanggalMulai &&
                                dataInvoice.tanggalBerakhir
                            ) {
                                const start = new Date(
                                    dataInvoice.tanggalMulai,
                                );
                                const end = new Date(
                                    dataInvoice.tanggalBerakhir,
                                );
                                const diffTime = Math.abs(
                                    end.getTime() - start.getTime(),
                                );
                                const diffDays = Math.ceil(
                                    diffTime / (1000 * 60 * 60 * 24),
                                );
                                const weeks = Math.ceil(diffDays / 7);
                                setNumberOfWeeksRental(weeks);
                            }
                        } else {
                            setJenisRuangan("Bulanan");
                        }
                    }

                    if (dataInvoice.tipeHarga === "custom") {
                        setValue(
                            "nominal",
                            dataInvoice.nominal?.toString() || "",
                        );
                        setIsCustomPrice(true);
                    } else {
                        updateNominalHarga(ruangan, uiTipeHarga);
                        setIsCustomPrice(false);
                    }
                }
            }

            setSelectedHarga(uiTipeHarga);

            if (dataInvoice.tanggalMulai && dataInvoice.tanggalBerakhir) {
                const start = new Date(dataInvoice.tanggalMulai);
                const end = new Date(dataInvoice.tanggalBerakhir);
                setDateRange([start, end]);

                // Log untuk verifikasi
                console.log(`📝 Edit Invoice - Periode:
                    Start: ${start.toLocaleDateString("id-ID")}
                    End: ${end.toLocaleDateString("id-ID")}
                    Selisih Bulan: ${calculateMonthsDifference(start, end)}
                `);
            }

            if (dataInvoice.tanggal) {
                setSelectedDate(new Date(dataInvoice.tanggal));
            }

            if (
                dataInvoice.jenisSewa?.toLowerCase() === "mingguan" &&
                dataInvoice.periodeMingguan
            ) {
                setNumberOfWeeksRental(dataInvoice.periodeMingguan);
            }

            if (dataInvoice.tanggalMulai && dataInvoice.tanggalBerakhir) {
                const startDate = new Date(dataInvoice.tanggalMulai);
                const endDate = new Date(dataInvoice.tanggalBerakhir);

                const startHour = startDate
                    .getHours()
                    .toString()
                    .padStart(2, "0");
                const startMinute = startDate
                    .getMinutes()
                    .toString()
                    .padStart(2, "0");
                const endHour = endDate.getHours().toString().padStart(2, "0");
                const endMinute = endDate
                    .getMinutes()
                    .toString()
                    .padStart(2, "0");

                const startTime = `${startHour}:${startMinute}`;
                const endTime = `${endHour}:${endMinute}`;

                setTimeRange([startTime, endTime]);

                setValue("jamMulai", startTime);
                setValue("jamBerakhir", endTime);
            }
        } else if (isOpenModalTambahInvoice && !dataInvoice) {
            reset(initialValues);
            setSelectedPenyewa(null);
            setSelectedRuangan(null);
            setSelectedHarga("Member");
            setIsCustomPrice(false);
            setJenisRuangan("Bulanan");
            setSelectedKategoriRuangan(null);
            setSearchRuangan("");
            setKapasitasRuangan("");
            setDetailHarga(null);

            // Set default untuk bulanan ke bulan ini
            const today = new Date();
            const startOfMonth = new Date(
                today.getFullYear(),
                today.getMonth(),
                1,
            );
            const endOfMonth = new Date(
                today.getFullYear(),
                today.getMonth() + 1,
                0,
            );
            setDateRange([startOfMonth, endOfMonth]);

            setSelectedDate(new Date());
            setTimeRange(["13:00", "15:00"]);
            setNumberOfWeeksRental(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOpenModalTambahInvoice,
        dataInvoice,
        reset,
        listPenyewa,
        listRoom,
        listRoomExclusive,
    ]);

    const handleCloseModal = () => {
        reset(initialValues);

        setSelectedPenyewa(null);
        setSelectedRuangan(null);
        setSelectedHarga("Member");
        setIsCustomPrice(false);
        setJenisRuangan("Bulanan");
        setSelectedKategoriRuangan(null);
        setSearchRuangan("");
        setKapasitasRuangan("");
        setDetailHarga(null);

        // Reset ke bulan ini
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
        );
        setDateRange([startOfMonth, endOfMonth]);

        setSelectedDate(new Date());
        setTimeRange(["13:00", "15:00"]);

        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 7);

        setSelectedWeekStart(today);
        setSelectedWeekEnd(endDate);
        setNumberOfWeeksRental(1);

        closeModalTambahInvoice();
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat("id-ID", {
            style: "currency",
            currency: "IDR",
            minimumFractionDigits: 0,
        }).format(amount);
    };

    return (
        <Dialog
            maxWidth="lg"
            fullScreen={isPhoneScreen}
            fullWidth={true}
            open={isOpenModalTambahInvoice}
            onClose={() => {}}
            scroll="body"
            PaperProps={{
                sx: {
                    borderRadius: 1,
                },
            }}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography variant="h6" fontWeight="bold">
                        {isReadOnly
                            ? "Detail Invoice"
                            : dataInvoice
                              ? "Edit Invoice"
                              : "Tambah Invoice"}
                    </Typography>

                    <IconButton onClick={closeModalTambahInvoice}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{ padding: isPhoneScreen ? undefined : 6 }}
            >
                <Stack gap={6}>
                    <Grid container alignItems="center" spacing={2}>
                        {/* Tanggal Invoice */}
                        <Grid item xs={12}>
                            <Typography mb={1}>Tanggal Invoice</Typography>
                            <Controller
                                name="tanggalInvoice"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        className="range-filter"
                                        dateFormat="dd/MM/yyyy"
                                        popperPlacement="top-start"
                                        disabled
                                        disabledKeyboardNavigation
                                        onFocus={(e) => e.target.blur()}
                                        selected={
                                            field.value
                                                ? new Date(field.value)
                                                : new Date()
                                        }
                                        onChange={(date) => {
                                            if (!isReadOnly) {
                                                field.onChange(date);
                                            }
                                        }}
                                        monthsShown={1}
                                        customInput={
                                            <Stack
                                                display="flex"
                                                justifyContent="space-between"
                                                direction="row"
                                                bgcolor="#A8B4AF66"
                                                height="60px"
                                                sx={{
                                                    border: errors.tanggalInvoice
                                                        ? "1px solid red"
                                                        : "1px solid #cbcbcb",
                                                    borderRadius: "4px",
                                                    alignItems: "center",
                                                    padding: 2,
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <Typography>
                                                    {field.value?.toLocaleString(
                                                        "id",
                                                        {
                                                            year: "numeric",
                                                            month: "long",
                                                            day: "numeric",
                                                        },
                                                    )}
                                                </Typography>
                                                <CalendarTodayIcon
                                                    sx={{
                                                        marginRight: "-9px",
                                                    }}
                                                />
                                            </Stack>
                                        }
                                    />
                                )}
                            />
                            {errors.tanggalInvoice && (
                                <FormHelperText error>
                                    {errors.tanggalInvoice.message}
                                </FormHelperText>
                            )}
                        </Grid>

                        {/* Pilih Penyewa */}
                        <Grid item xs={6} sx={{ mt: "25px" }}>
                            <Typography mb={1}>Pilih Penyewa</Typography>
                            <Autocomplete
                                options={listPenyewa?.content ?? []}
                                getOptionLabel={(option) =>
                                    option.namaPerusahaan ?? ""
                                }
                                isOptionEqualToValue={(option, value) =>
                                    String(option.id) === String(value?.id)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Penyewa"
                                        variant="outlined"
                                        error={Boolean(errors.penyewaId)}
                                        helperText={
                                            errors.penyewaId
                                                ? errors.penyewaId.message
                                                : " "
                                        }
                                        disabled={isReadOnly}
                                    />
                                )}
                                fullWidth
                                value={selectedPenyewa}
                                onChange={(_, newValue) => {
                                    if (!isReadOnly) {
                                        setSelectedPenyewa(newValue);
                                        if (newValue) {
                                            setValue("penyewaId", newValue.id);
                                            clearErrors("penyewaId");
                                        } else {
                                            setValue("penyewaId", 0);
                                        }
                                    }
                                }}
                                disabled={isReadOnly}
                            />
                        </Grid>

                        {/* Pilih Kategori Ruangan */}
                        <Grid item xs={6}>
                            <Typography mb={1}>
                                Pilih Kategori Ruangan
                            </Typography>
                            <Autocomplete
                                options={["Ruangan Biasa", "Ruangan Khusus"]}
                                getOptionLabel={(option) => option}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Pilih Kategori Ruangan"
                                        variant="outlined"
                                        disabled={isReadOnly}
                                    />
                                )}
                                fullWidth
                                value={selectedKategoriRuangan}
                                onChange={(_, newValue) => {
                                    if (!isReadOnly) {
                                        setSelectedKategoriRuangan(newValue);
                                        setSelectedRuangan(null);
                                        setValue("ruanganId", 0);
                                        setKapasitasRuangan("");
                                        setSearchRuangan("");
                                        setDetailHarga(null);
                                        if (newValue) {
                                            clearErrors("ruanganId");
                                        }
                                    }
                                }}
                                disabled={isReadOnly}
                            />
                        </Grid>

                        {/* Pilih Ruangan dengan Pencarian */}
                        <Grid item xs={6} sx={{ mt: "25px" }}>
                            <Typography mb={1}>Pilih Ruangan</Typography>
                            <Autocomplete
                                options={filteredRuangan}
                                getOptionLabel={(option) => {
                                    if (option.jenisSewa === "Jam") {
                                        return `${option.nama} (Jam)`;
                                    } else if (
                                        option.jenisSewa === "Mingguan"
                                    ) {
                                        return `${option.nama} (Mingguan)`;
                                    } else if (option.jenisSewa === "Bulanan") {
                                        return `${option.nama} (Bulanan)`;
                                    } else {
                                        return option.nama;
                                    }
                                }}
                                isOptionEqualToValue={(option, value) =>
                                    String(option.id) === String(value?.id)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Ruangan"
                                        variant="outlined"
                                        error={Boolean(errors.ruanganId)}
                                        helperText={
                                            errors.ruanganId
                                                ? errors.ruanganId.message
                                                : " "
                                        }
                                        disabled={
                                            isReadOnly ||
                                            !selectedKategoriRuangan
                                        }
                                        onChange={(e) => {
                                            if (!isReadOnly) {
                                                setSearchRuangan(
                                                    e.target.value,
                                                );
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            type: "search",
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Box>
                                            <Typography variant="body1">
                                                {option.nama}
                                                {option.jenisSewa === "Jam" &&
                                                    " (Jam)"}
                                                {option.jenisSewa ===
                                                    "Mingguan" && " (Mingguan)"}
                                                {option.jenisSewa ===
                                                    "Bulanan" && " (Bulanan)"}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Kapasitas: {option.kapasitas}{" "}
                                                orang
                                                {option.jenisSewa !==
                                                    "Bulanan" &&
                                                    ` • Sewa ${option.jenisSewa}`}
                                                {option.isKhusus &&
                                                    " • Ruangan Khusus"}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                fullWidth
                                value={selectedRuangan}
                                onChange={(_, newValue) =>
                                    handleRuanganChange(newValue)
                                }
                                disabled={
                                    isReadOnly || !selectedKategoriRuangan
                                }
                                filterOptions={(x) => x}
                            />
                        </Grid>

                        {/* Pax - Menampilkan kapasitas ruangan yang dipilih (hanya info) */}
                        <Grid item xs={6}>
                            <Typography mb={1}>Pax</Typography>
                            <TextField
                                value={kapasitasRuangan}
                                multiline
                                rows={1}
                                disabled
                                placeholder="Kapasitas ruangan akan muncul di sini"
                                fullWidth
                            />
                        </Grid>

                        {/* PERBAIKAN: Periode untuk Bulanan - TAMPILAN TANGGAL LENGKAP */}
                        {jenisRuangan === "Bulanan" && (
                            <>
                                <Grid item xs={6}>
                                    <Typography mb={1}>
                                        Periode Sewa (Bulanan)
                                    </Typography>
                                    <DatePicker
                                        className="range-filter"
                                        dateFormat="dd/MM/yyyy"
                                        selectsRange={true}
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={handleMonthlyChange}
                                        monthsShown={
                                            isPhoneScreen || isTabletScreen
                                                ? 1
                                                : 2
                                        }
                                        popperProps={{
                                            modifiers: [
                                                {
                                                    name: "preventOverflow",
                                                    options: {
                                                        boundary: "viewport",
                                                    },
                                                },
                                                {
                                                    name: "zIndexFix",
                                                    enabled: true,
                                                    phase: "write",
                                                    fn: ({
                                                        state,
                                                    }: ModifierArguments<any>) => {
                                                        state.styles.popper.zIndex =
                                                            "1500";
                                                    },
                                                },
                                            ],
                                        }}
                                        disabled={isReadOnly}
                                        customInput={
                                            <CustomInput
                                                value={
                                                    startDate && endDate
                                                        ? `${startDate.toLocaleDateString(
                                                              "id-ID",
                                                              {
                                                                  day: "numeric",
                                                                  month: "long",
                                                                  year: "numeric",
                                                              },
                                                          )} - ${endDate.toLocaleDateString(
                                                              "id-ID",
                                                              {
                                                                  day: "numeric",
                                                                  month: "long",
                                                                  year: "numeric",
                                                              },
                                                          )}`
                                                        : "Pilih Periode"
                                                }
                                                error={Boolean(
                                                    errors.tanggalMulai ||
                                                    errors.tanggalBerakhir,
                                                )}
                                                disabled={isReadOnly}
                                                placeholder="Pilih Periode Bulanan"
                                            />
                                        }
                                    />
                                    {(errors.tanggalMulai ||
                                        errors.tanggalBerakhir) && (
                                        <FormHelperText error>
                                            {errors.tanggalMulai?.message ||
                                                errors.tanggalBerakhir?.message}
                                        </FormHelperText>
                                    )}
                                </Grid>

                                <Grid item xs={3}>
                                    <Typography mb={1}>Jam Mulai</Typography>

                                    <Autocomplete
                                        options={timeOptions}
                                        value={timeRange[0]}
                                        onChange={(_, newValue) => {
                                            if (!isReadOnly && newValue) {
                                                setTimeRange([
                                                    newValue,
                                                    timeRange[1],
                                                ]);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Jam Mulai"
                                                variant="outlined"
                                            />
                                        )}
                                        disabled={isReadOnly}
                                    />

                                    {errors.jamMulai && (
                                        <FormHelperText error>
                                            {errors.jamMulai?.message ||
                                                errors.jamBerakhir?.message}
                                        </FormHelperText>
                                    )}
                                </Grid>

                                <Grid item xs={3}>
                                    <Typography mb={1}>Jam Akhir</Typography>

                                    <Autocomplete
                                        options={timeOptions}
                                        value={timeRange[1]}
                                        onChange={(_, newValue) => {
                                            if (!isReadOnly && newValue) {
                                                setTimeRange([
                                                    timeRange[0],
                                                    newValue,
                                                ]);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Jam Berakhir"
                                                variant="outlined"
                                            />
                                        )}
                                        disabled={isReadOnly}
                                    />
                                    {errors.jamBerakhir && (
                                        <FormHelperText error>
                                            {errors.jamMulai?.message ||
                                                errors.jamBerakhir?.message}
                                        </FormHelperText>
                                    )}
                                </Grid>
                            </>
                        )}

                        {/* Periode untuk Mingguan - DatePicker dengan Filter Mingguan */}
                        {jenisRuangan === "Mingguan" && (
                            <>
                                <Grid item xs={3}>
                                    <Typography mb={1}>
                                        Pilih Hari Mulai (Mingguan)
                                    </Typography>
                                    <Controller
                                        name="tanggalMulai"
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                                className="range-filter"
                                                dateFormat="dd/MM/yyyy"
                                                popperPlacement="top-start"
                                                disabledKeyboardNavigation
                                                onFocus={(e) => e.target.blur()}
                                                selected={
                                                    field.value
                                                        ? new Date(field.value)
                                                        : selectedWeekStart
                                                }
                                                onChange={(date) => {
                                                    if (!isReadOnly && date) {
                                                        handleWeeklyPeriodChange(
                                                            date,
                                                        );
                                                        field.onChange(
                                                            date.toISOString(),
                                                        );
                                                    }
                                                }}
                                                monthsShown={1}
                                                filterDate={(date) =>
                                                    !isReadOnly
                                                }
                                                customInput={
                                                    <Stack
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        direction="row"
                                                        height="60px"
                                                        sx={{
                                                            border: errors.tanggalMulai
                                                                ? "1px solid red"
                                                                : "1px solid #cbcbcb",
                                                            borderRadius: "4px",
                                                            alignItems:
                                                                "center",
                                                            padding: 2,
                                                            cursor: isReadOnly
                                                                ? "default"
                                                                : "pointer",
                                                            backgroundColor:
                                                                isReadOnly
                                                                    ? "#f5f5f5"
                                                                    : "#ffffff",
                                                        }}
                                                    >
                                                        <Typography>
                                                            {field.value
                                                                ? new Date(
                                                                      field.value,
                                                                  ).toLocaleDateString(
                                                                      "id",
                                                                      {
                                                                          year: "numeric",
                                                                          month: "long",
                                                                          day: "numeric",
                                                                      },
                                                                  )
                                                                : selectedWeekStart
                                                                  ? selectedWeekStart.toLocaleDateString(
                                                                        "id",
                                                                        {
                                                                            year: "numeric",
                                                                            month: "long",
                                                                            day: "numeric",
                                                                        },
                                                                    )
                                                                  : "Pilih Tanggal Mulai"}
                                                        </Typography>
                                                        <CalendarTodayIcon
                                                            sx={{
                                                                marginRight:
                                                                    "-9px",
                                                            }}
                                                        />
                                                    </Stack>
                                                }
                                                disabled={isReadOnly}
                                            />
                                        )}
                                    />
                                    {errors.tanggalMulai && (
                                        <FormHelperText error>
                                            {errors.tanggalMulai.message}
                                        </FormHelperText>
                                    )}
                                </Grid>

                                <Grid item xs={3}>
                                    <Typography mb={1}>
                                        Jumlah Minggu
                                    </Typography>
                                    <Autocomplete
                                        options={[1, 2, 3, 4, 5, 6, 7, 8]}
                                        getOptionLabel={(option) =>
                                            `${option} Minggu`
                                        }
                                        value={numberOfWeeksRental}
                                        onChange={(_, newValue) => {
                                            if (!isReadOnly && newValue) {
                                                handleNumberOfWeeksChange(
                                                    Number(newValue),
                                                );
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Pilih Jumlah Minggu"
                                                variant="outlined"
                                            />
                                        )}
                                        disabled={
                                            isReadOnly || !selectedWeekStart
                                        }
                                    />
                                </Grid>

                                <Grid item xs={3}>
                                    <Typography mb={1}>Jam Mulai</Typography>
                                    <Autocomplete
                                        options={timeOptions}
                                        value={timeRange[0]}
                                        onChange={(_, newValue) => {
                                            if (!isReadOnly && newValue) {
                                                setTimeRange([
                                                    newValue,
                                                    timeRange[1],
                                                ]);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Jam Mulai"
                                                variant="outlined"
                                            />
                                        )}
                                        disabled={isReadOnly}
                                    />
                                    {errors.jamMulai && (
                                        <FormHelperText error>
                                            {errors.jamMulai?.message}
                                        </FormHelperText>
                                    )}
                                </Grid>

                                <Grid item xs={3}>
                                    <Typography mb={1}>Jam Akhir</Typography>
                                    <Autocomplete
                                        options={timeOptions}
                                        value={timeRange[1]}
                                        onChange={(_, newValue) => {
                                            if (!isReadOnly && newValue) {
                                                setTimeRange([
                                                    timeRange[0],
                                                    newValue,
                                                ]);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Jam Akhir"
                                                variant="outlined"
                                            />
                                        )}
                                        disabled={isReadOnly}
                                    />
                                    {errors.jamBerakhir && (
                                        <FormHelperText error>
                                            {errors.jamBerakhir?.message}
                                        </FormHelperText>
                                    )}
                                </Grid>
                            </>
                        )}

                        {/* Tanggal dan Jam untuk Ruangan Jam */}
                        {jenisRuangan === "Jam" && (
                            <>
                                <Grid item xs={6}>
                                    <Typography mb={1}>Tanggal</Typography>
                                    <DatePicker
                                        selected={selectedDate}
                                        onChange={(date) => {
                                            if (!isReadOnly) {
                                                setSelectedDate(date);
                                                if (date) {
                                                    setValue(
                                                        "tanggal",
                                                        date.toISOString(),
                                                    );
                                                    clearErrors("tanggal");
                                                }
                                            }
                                        }}
                                        dateFormat="dd/MM/yyyy"
                                        customInput={
                                            <CustomInput
                                                value={
                                                    selectedDate
                                                        ? selectedDate.toLocaleDateString(
                                                              "id-ID",
                                                          )
                                                        : ""
                                                }
                                                error={Boolean(errors.tanggal)}
                                                disabled={isReadOnly}
                                                placeholder="Pilih Tanggal"
                                            />
                                        }
                                        disabled={isReadOnly}
                                        popperProps={{
                                            modifiers: [
                                                {
                                                    name: "preventOverflow",
                                                    options: {
                                                        boundary: "viewport",
                                                    },
                                                },
                                                {
                                                    name: "zIndexFix",
                                                    enabled: true,
                                                    phase: "write",
                                                    fn: ({
                                                        state,
                                                    }: ModifierArguments<any>) => {
                                                        state.styles.popper.zIndex =
                                                            "1500";
                                                    },
                                                },
                                            ],
                                        }}
                                    />
                                    {errors.tanggal && (
                                        <FormHelperText error>
                                            {errors.tanggal.message}
                                        </FormHelperText>
                                    )}
                                </Grid>

                                <Grid item xs={3}>
                                    <Typography mb={1}>Jam Mulai</Typography>

                                    <Autocomplete
                                        options={timeOptions}
                                        value={timeRange[0]}
                                        onChange={(_, newValue) => {
                                            if (!isReadOnly && newValue) {
                                                setTimeRange([
                                                    newValue,
                                                    timeRange[1],
                                                ]);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Jam Mulai"
                                                variant="outlined"
                                            />
                                        )}
                                        disabled={isReadOnly}
                                    />

                                    {errors.jamMulai && (
                                        <FormHelperText error>
                                            {errors.jamMulai?.message ||
                                                errors.jamBerakhir?.message}
                                        </FormHelperText>
                                    )}
                                </Grid>

                                <Grid item xs={3}>
                                    <Typography mb={1}>Jam Akhir</Typography>

                                    <Autocomplete
                                        options={timeOptions}
                                        value={timeRange[1]}
                                        onChange={(_, newValue) => {
                                            if (!isReadOnly && newValue) {
                                                setTimeRange([
                                                    timeRange[0],
                                                    newValue,
                                                ]);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Jam Berakhir"
                                                variant="outlined"
                                            />
                                        )}
                                        disabled={isReadOnly}
                                    />
                                    {errors.jamBerakhir && (
                                        <FormHelperText error>
                                            {errors.jamMulai?.message ||
                                                errors.jamBerakhir?.message}
                                        </FormHelperText>
                                    )}
                                </Grid>
                            </>
                        )}

                        {/* Pilih Harga dan Nominal Tagihan */}
                        <Grid item xs={6}>
                            <Typography mb={1}>Pilih Harga</Typography>
                            <Autocomplete
                                options={["Member", "Non Member", "Custom"]}
                                getOptionLabel={(option) => option}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        error={Boolean(errors.tipeHarga)}
                                        helperText={
                                            errors.tipeHarga
                                                ? errors.tipeHarga.message
                                                : " "
                                        }
                                        disabled={isReadOnly}
                                    />
                                )}
                                value={selectedHarga}
                                onChange={(_, newValue) => {
                                    if (!isReadOnly) {
                                        setSelectedHarga(newValue);

                                        if (newValue) {
                                            setValue("tipeHarga", newValue);
                                            clearErrors("tipeHarga");

                                            if (newValue === "Custom") {
                                                setIsCustomPrice(true);
                                                setValue("nominal", "");
                                                setDetailHarga(null);
                                            } else {
                                                setIsCustomPrice(false);
                                                if (selectedRuangan) {
                                                    updateNominalHarga(
                                                        selectedRuangan,
                                                        newValue,
                                                    );
                                                }
                                            }
                                        } else {
                                            setValue("tipeHarga", "");
                                            setValue("nominal", "");
                                            setIsCustomPrice(false);
                                            setDetailHarga(null);
                                        }
                                    }
                                }}
                                disabled={isReadOnly}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Typography mb={1}>Nominal Tagihan</Typography>
                            <Controller
                                name="nominal"
                                control={control}
                                render={({ field }) => (
                                    <NumberFormat
                                        customInput={TextField}
                                        id="nominal"
                                        placeholder="500.000"
                                        size="medium"
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        fullWidth
                                        variant="outlined"
                                        value={field.value}
                                        disabled={isReadOnly || !isCustomPrice}
                                        onValueChange={(values) => {
                                            const { value } = values;
                                            setValue("nominal", value);
                                            if (value && isCustomPrice) {
                                                clearErrors("nominal");
                                            }
                                        }}
                                        error={Boolean(errors.nominal)}
                                        helperText={
                                            errors.nominal
                                                ? errors.nominal.message
                                                : " "
                                        }
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography>Rp.</Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Detail Informasi Harga */}
                        {detailHarga && (
                            <Grid item xs={12}>
                                <Card
                                    variant="outlined"
                                    sx={{ bgcolor: "#f8f9fa" }}
                                >
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Detail Harga
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={3}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Harga per{" "}
                                                    {detailHarga.satuan}:
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="bold"
                                                >
                                                    {selectedHarga === "Custom"
                                                        ? "Custom"
                                                        : formatCurrency(
                                                              detailHarga.hargaPerUnit,
                                                          )}{" "}
                                                    {selectedHarga !==
                                                        "Custom" &&
                                                        `/ ${detailHarga.satuan}`}
                                                </Typography>
                                                {selectedHarga === "Custom" &&
                                                    detailHarga.hargaPerUnit >
                                                        0 && (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            (
                                                            {formatCurrency(
                                                                detailHarga.hargaPerUnit,
                                                            )}{" "}
                                                            /{" "}
                                                            {detailHarga.satuan}
                                                            )
                                                        </Typography>
                                                    )}
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Jumlah {detailHarga.satuan}:
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="bold"
                                                >
                                                    {detailHarga.jumlahUnit}{" "}
                                                    {detailHarga.satuan}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Periode:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {detailHarga.keterangan}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Total Harga:
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    color="primary"
                                                    fontWeight="bold"
                                                >
                                                    {formatCurrency(
                                                        detailHarga.totalHarga,
                                                    )}
                                                </Typography>
                                                {selectedHarga === "Custom" && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mt: 1 }}
                                                    >
                                                        * Harga custom
                                                        diterapkan
                                                    </Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Catatan */}
                        <Grid item xs={12}>
                            <Typography fontSize={18} fontWeight={500}>
                                Catatan
                            </Typography>
                            <Controller
                                name="keterangan"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Masukan Catatan"
                                        multiline
                                        rows={2}
                                        fullWidth
                                        disabled={isReadOnly}
                                        InputProps={{
                                            readOnly: isReadOnly,
                                        }}
                                        sx={{
                                            mt: 1,
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    {/* Tombol Aksi */}
                    {!isReadOnly && (
                        <Stack
                            direction="row"
                            spacing={1.5}
                            width="100%"
                            sx={{
                                justifyContent: "flex-end",
                                alignItems: "center",
                            }}
                        >
                            <LoadingButton
                                loadingPosition="center"
                                variant="outlined"
                                onClick={handleCloseModal}
                            >
                                Kembali
                            </LoadingButton>

                            <LoadingButton
                                loading={isButtonLoading || mutation.isLoading}
                                loadingPosition="center"
                                variant="contained"
                                onClick={handleSubmit(onSubmit)}
                                disabled={isButtonLoading || mutation.isLoading}
                                data-testid="simpan-invoice"
                            >
                                Simpan
                            </LoadingButton>
                        </Stack>
                    )}

                    {isReadOnly && (
                        <Stack
                            direction="row"
                            spacing={1.5}
                            width="100%"
                            sx={{
                                justifyContent: "flex-end",
                                alignItems: "center",
                            }}
                        >
                            <LoadingButton
                                loadingPosition="center"
                                variant="outlined"
                                onClick={handleCloseModal}
                            >
                                Tutup
                            </LoadingButton>
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default ModalTambahInvoice;
